import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { toast } from "sonner";
import { Check, Crown, Loader2, Plus, Sparkles, Tag, X, Zap } from "lucide-react";

const tierIcon = { free: Sparkles, starter: Zap, pro: Crown, lifetime: Crown };

export default function PricingPanel() {
  const { active, refresh, botInviteUrl } = useServer();
  const [tiers, setTiers] = useState([]);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState("");

  // Promo code state
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // { code, discount_percent }
  const [promoChecking, setPromoChecking] = useState(false);

  useEffect(() => { api.get("/subscriptions/tiers").then((r) => setTiers(r.data)); }, []);

  useEffect(() => {
    if (active?.bot_present) {
      api.get(`/subscriptions/${active.id}`).then((r) => setSub(r.data));
    } else {
      setSub(null);
    }
  }, [active?.id, active?.bot_present]);

  const applyPromo = async (forTier) => {
    const code = (promoCode || "").trim().toUpperCase();
    if (!code) { toast.error("Enter a promo code"); return; }
    setPromoChecking(true);
    try {
      const url = forTier ? `/promo/validate/${code}?tier=${forTier}` : `/promo/validate/${code}`;
      const { data } = await api.get(url);
      setPromoApplied({ code: data.code, discount_percent: data.discount_percent });
      toast.success(`Promo ${data.code} applied — ${data.discount_percent}% off`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Invalid promo code";
      toast.error(msg);
      setPromoApplied(null);
    } finally {
      setPromoChecking(false);
    }
  };

  const removePromo = () => {
    setPromoApplied(null);
    setPromoCode("");
  };

  const upgrade = async (tier) => {
    if (!active) {
      toast.error("Select a server first");
      return;
    }
    if (!active.bot_present) {
      toast.error("Add the bot to this server first");
      if (botInviteUrl) window.open(botInviteUrl, "_blank");
      return;
    }
    // If user typed a code but didn't apply it, validate now
    let promo = promoApplied;
    if (!promo && promoCode.trim()) {
      try {
        const { data } = await api.get(`/promo/validate/${promoCode.trim().toUpperCase()}?tier=${tier}`);
        promo = { code: data.code, discount_percent: data.discount_percent };
        setPromoApplied(promo);
      } catch {
        toast.error("Promo code is invalid for this tier");
        return;
      }
    }
    setBusy(tier);
    try {
      const payload = { tier };
      if (promo) payload.promo_code = promo.code;
      const { data } = await api.post(`/subscriptions/${active.id}`, payload);
      setSub(data.subscription);
      toast.success(
        promo
          ? `Tier set to ${tier.toUpperCase()} with ${promo.discount_percent}% off via ${promo.code}`
          : `Tier set to ${tier.toUpperCase()}`
      );
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upgrade failed");
    } finally {
      setBusy("");
    }
  };

  const priceWithDiscount = (price) => {
    if (!promoApplied || !price) return null;
    const after = price * (1 - promoApplied.discount_percent / 100);
    return Math.round(after * 100) / 100;
  };

  return (
    <div data-testid="pricing-panel" className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline mb-2">// SUBSCRIPTIONS</div>
          <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Pricing</h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">
            Each Discord server has its own subscription. Free tier lets you try the bot —
            upgrade to unlock AI, larger queue, and custom welcomes.
          </p>
        </div>
        {active && (
          <div className="tactical-card px-5 py-3">
            <div className="overline mb-1">CURRENT</div>
            <div className="font-heading text-xl font-bold uppercase">
              {sub?.tier?.toUpperCase() || (active.bot_present ? "FREE" : "BOT NOT ADDED")}
            </div>
            <div className="text-xs text-zinc-500 truncate">on {active.name}</div>
          </div>
        )}
      </div>

      {!active?.bot_present && (
        <div className="tactical-card p-6 border-yellow-500/40 bg-yellow-500/5">
          <div className="overline text-yellow-400 mb-1">// BOT NOT INSTALLED</div>
          <h3 className="font-heading text-lg font-bold uppercase mb-2">
            Add the bot to {active?.name || "your server"} to subscribe
          </h3>
          <p className="text-sm text-zinc-400 mb-4">
            Install the 606 Bot first, then come back to choose a plan.
          </p>
          {botInviteUrl && (
            <a href={botInviteUrl} target="_blank" rel="noreferrer" className="btn-primary" data-testid="pricing-add-bot-btn">
              <Plus className="w-4 h-4" /> Add Bot to Server
            </a>
          )}
        </div>
      )}

      {active?.bot_present && (
        <div className="tactical-card p-4 flex flex-wrap items-center gap-3" data-testid="promo-code-row">
          <Tag className="w-4 h-4 text-brand-orange shrink-0" />
          <div className="overline shrink-0">PROMO CODE</div>
          {promoApplied ? (
            <div className="flex items-center gap-2 flex-1">
              <div className="font-mono-tabular text-sm bg-emerald-500/10 border border-emerald-500/40 text-emerald-400 px-3 py-1.5 rounded">
                {promoApplied.code} · −{promoApplied.discount_percent}%
              </div>
              <button onClick={removePromo} className="text-zinc-400 hover:text-white" data-testid="promo-remove">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <input
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                placeholder="GAMER20"
                className="bg-brand-elevated border border-brand-line rounded px-3 py-1.5 text-sm font-mono-tabular flex-1 max-w-xs focus:border-brand-orange focus:outline-none"
                data-testid="promo-code-input"
              />
              <button
                onClick={() => applyPromo()}
                disabled={promoChecking}
                className="btn-secondary text-xs"
                data-testid="promo-apply-btn"
              >
                {promoChecking ? "Checking…" : "Apply"}
              </button>
            </>
          )}
          <span className="ml-auto text-xs text-zinc-500 hidden md:inline">Have a code? Apply it before choosing a plan.</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {tiers.map((t) => {
          const Icon = tierIcon[t.id] || Sparkles;
          const popular = t.id === "pro";
          const current = sub?.tier === t.id;
          return (
            <div
              key={t.id}
              className={`tactical-card p-6 flex flex-col relative ${popular ? "tier-popular" : ""}`}
              data-testid={`pricing-tier-${t.id}`}
            >
              {popular && (
                <div className="absolute -top-3 left-6 px-3 py-1 bg-brand-orange text-black font-heading text-xs uppercase tracking-widest rounded">
                  MOST POPULAR
                </div>
              )}
              {current && (
                <div className="absolute -top-3 right-6 px-3 py-1 border border-emerald-500 bg-black text-emerald-400 font-heading text-xs uppercase tracking-widest rounded">
                  ACTIVE
                </div>
              )}
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-brand-orange/10 border border-brand-orange/40 rounded-lg">
                  <Icon className="w-5 h-5 text-brand-orange" />
                </div>
                <div className="font-heading text-2xl font-bold uppercase tracking-tight">{t.label}</div>
              </div>
              <div className="mb-5">
                {promoApplied && t.price > 0 ? (
                  <>
                    <span className="font-mono-tabular text-zinc-500 line-through text-2xl mr-2">${t.price}</span>
                    <span className="font-mono-tabular text-5xl font-bold text-brand-orange">${priceWithDiscount(t.price)}</span>
                  </>
                ) : (
                  <span className="font-mono-tabular text-5xl font-bold">${t.price}</span>
                )}
                <span className="text-zinc-500 text-sm ml-2">/{t.period}</span>
                {promoApplied && t.price > 0 && (
                  <div className="mt-1.5 text-[10px] font-mono-tabular text-emerald-400 uppercase">
                    {promoApplied.code} APPLIED · {promoApplied.discount_percent}% OFF
                  </div>
                )}
              </div>
              <ul className="space-y-2.5 mb-6 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-brand-orange shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => upgrade(t.id)}
                disabled={busy === t.id || current}
                className={current ? "btn-secondary justify-center" : "btn-primary justify-center"}
                data-testid={`pricing-cta-${t.id}`}
              >
                {busy === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {current ? "Current Plan" : t.price === 0 ? "Use Free" : "Choose Plan"}
              </button>
            </div>
          );
        })}
      </div>

      <div className="tactical-card p-5 border-brand-line">
        <div className="font-mono-tabular text-xs text-zinc-500">
          ⚠ PAYMENT WIRING IS PENDING — choosing a tier applies it immediately for now. Stripe integration will be added next.
        </div>
      </div>
    </div>
  );
}
